import * as React from "react";

import iso from "iso-3166-1";
import { Check, ChevronsUpDown, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/** ISO 3166-1 alpha-2 country code → flag emoji (regional indicator symbols). */
function flagEmoji(code: string): string {
  const upper = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return "";
  const base = 0x1f1e6; // regional indicator "A"
  return String.fromCodePoint(
    base + (upper.charCodeAt(0) - 65),
    base + (upper.charCodeAt(1) - 65),
  );
}

const COUNTRIES = iso
  .all()
  .map((c) => ({ code: c.alpha2, name: c.country }))
  .sort((a, b) => a.name.localeCompare(b.name));

interface CountrySelectProps {
  /** Selected ISO 3166-1 alpha-2 country code (uppercase), or undefined. */
  value?: string;
  onValueChanged: (code: string | undefined) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  parentClassName?: string;
  className?: string;
  /** Put on the trigger button, so an external `<label htmlFor>` can point at it. */
  triggerId?: string;
  /** Id of the element naming this control — role="combobox" takes no name from its content. */
  ariaLabelledBy?: string;
}

export function CountrySelect({
  value,
  onValueChanged,
  label,
  placeholder = "Any country",
  disabled = false,
  parentClassName,
  className,
  triggerId,
  ariaLabelledBy,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);

  const selected = value
    ? COUNTRIES.find((c) => c.code === value.toUpperCase())
    : undefined;

  return (
    <div
      className={cn("flex flex-col space-y-2 text-foreground", parentClassName)}
    >
      {label && <div className="text-sm font-medium">{label}</div>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            id={triggerId}
            aria-labelledby={ariaLabelledBy}
            className={cn("justify-between font-normal", className)}
            disabled={disabled}
          >
            <span className="truncate">
              {selected
                ? `${flagEmoji(selected.code)} ${selected.name}`
                : placeholder}
            </span>
            <div className="ml-2 flex shrink-0 items-center">
              {value && (
                <XIcon
                  className="h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onValueChanged(undefined);
                  }}
                />
              )}
              <ChevronsUpDown className="ml-1 h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="bottom" className="w-[300px] p-0">
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty className="p-2 justify-center">
                No country found
              </CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={`${country.name} ${country.code}`}
                    onSelect={() => {
                      onValueChanged(
                        country.code === value ? undefined : country.code,
                      );
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === country.code ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="mr-2">{flagEmoji(country.code)}</span>
                    {country.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
